import { acceptHMRUpdate, defineStore } from 'pinia'
import type { Pagination, Plan, SubscriberUsingAttemptRequest, Subscription, SubscriptionAttempt } from '@/types'
import type { CancelSubscriptionPayload, GetSubscriptionForm } from '@/types/forms/Subscription.ts'
import { SUP_PAYMENT_METHODS } from '@/enum/payments.ts'

export const useSubscriptionsStore = defineStore('subscriptions', () => {
  const plans = ref<Plan[]>([])
  const planImages = ref<string[]>([])
  const subscription = ref<Subscription | null>(null)
  const attempts = ref(new Map<number, SubscriptionAttempt>())
  const authStore = useAuthStore()
  const loading = ref(false)
  const error = ref<string | null>(null)
  const didFetchPlans = ref(false)
  const plansWaitingRequest = useWaitingRequest<[Plan[], string[]]>()
  const couponCode = ref<string| undefined>(undefined)

  const createAttempt = async (planPaymentMethodID: number) => {
    try {
      loading.value = true
      const { data } = await useForm<{
        plan_payment_method_id: number,
        coupon_code? : string | undefined,
      }, SubscriptionAttempt>('post', '/subscriptions/attempts', {
        plan_payment_method_id: planPaymentMethodID,
        coupon_code: couponCode.value,
      }).submit()
      loading.value = true
      attempts.value.set(planPaymentMethodID, data)
      return data
    } finally {
      loading.value = false
    }
  }

  const getOrCreateAttempt = (planPaymentMethodID: number) => {
    const attempt = attempts.value.get(planPaymentMethodID)
    if (attempt) {
      return attempt
    }

    return createAttempt(planPaymentMethodID)
  }

  const fetchPlans = async (excludedSubPaymentMethodIds: number[] = []) => {
    if (didFetchPlans.value) {
      return [plans.value, planImages.value]
    }

    attempts.value.clear()

    const excluded = [...excludedSubPaymentMethodIds]
    if (typeof window.ApplePaySession !== 'function') {
      excluded.push(SUP_PAYMENT_METHODS.applePay)
    }
    excluded.push(SUP_PAYMENT_METHODS.apple_in_app_purchases)
    excluded.push(SUP_PAYMENT_METHODS.google_in_app_purchases)

    try {
      loading.value = true

      const [fetchedPlans, images] = await plansWaitingRequest.execute(async () => {
        const response = await useForm<Record<string, unknown>, Plan[]>('get', '/subscriptions/plans', {
          filter: {
            excluded_sub_payment_method_ids: excluded,
            coupon_code: couponCode.value,
          },
        }).submit()

        didFetchPlans.value = true
        return [response.data, loadImages(response.data)]
      })

      plans.value = fetchedPlans
      planImages.value = images
      return [fetchedPlans, images]
    } finally {
      loading.value = false
    }
  }
  const setCouponCode = (coupon_code: string | undefined) => {
    couponCode.value = coupon_code
    didFetchPlans.value = false
  }

  const couponForm = useForm<{
    filter: {
      excluded_sub_payment_method_ids?: number[],
      coupon_code?: string
    }
    'filter.excluded_sub_payment_method_ids'?: number[]
    'filter.coupon_code'?: string
  }, Plan[]>('get', '/subscriptions/plans', {
    filter: {
      excluded_sub_payment_method_ids: undefined,
      coupon_code: undefined,
    },
  })

  const getCouponForm = () => {
    return couponForm
  }

  const loadImages = (plans: Plan[]) => {
    const images = new Set<string>()
    plans.forEach(plan => {
      plan.plan_payment_methods.forEach(planPaymentMethod => {
        if (planPaymentMethod.sub_payment_method?.media) {
          images.add(planPaymentMethod.sub_payment_method.media.url)
        }
      })
    })

    const out: string[] = []

    for (const image of images.values()) {
      out.push(image)
    }

    return out
  }

  const createSubscription = async (attempt: SubscriptionAttempt, requestData: SubscriberUsingAttemptRequest) => {
    try {
      loading.value = true
      const { data } = await useForm<SubscriberUsingAttemptRequest, Subscription>('post', `/subscriptions/attempts/${attempt.id}`, requestData).submit()

      subscription.value = data
      authStore.user = data?.user

      return data
    } finally {
      loading.value = false
    }
  }

  const retryAttempt = async (attempt: SubscriptionAttempt) => {
    try {
      loading.value = true
      const { data } = await useForm<SubscriberUsingAttemptRequest, Subscription>('post', `/subscriptions/attempts/${attempt.id}/retry`, {}).submit()

      subscription.value = data
      authStore.user!.has_valid_subscription = true

      return data
    } finally {
      loading.value = false
    }
  }

  const fetchSubscriptions = async (filters: Partial<GetSubscriptionForm> = {}) => {
    try {
      loading.value = true
      return useForm<Partial<GetSubscriptionForm>, Pagination<Subscription>>('get', 'subscriptions', filters).submit()
    } finally {
      loading.value = false
    }
  }

  const cancelSubscription = async (cancelPayload: CancelSubscriptionPayload) => {
    try {
      loading.value = true
      const result = await useForm<CancelSubscriptionPayload>('post', `subscriptions/cancel`, cancelPayload).submit()
      await authStore.reAuthenticate()
      return result
    } finally {
      loading.value = false
    }
  }

  const hasPlans = async () => {
    return true // no need to request everytime, we have plans everywhere
    // if (didFetchPlans.value) {
    //   return plans.value.length > 0
    // }
    //
    // try {
    //   await fetchPlans()
    //   return plans.value.length > 0
    // } catch {
    //   return false
    // }
  }

  return {
    // state
    plans,
    couponCode,
    planImages,
    subscription,
    attempts,
    loading,
    error,
    // actions
    createAttempt,
    retryAttempt,
    fetchPlans,
    loadImages,
    createSubscription,
    getOrCreateAttempt,
    fetchSubscriptions,
    cancelSubscription,
    hasPlans,
    setCouponCode,
    getCouponForm,
  }
})

// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSubscriptionsStore, import.meta.hot))
}
